import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, ExternalLink, AlertTriangle, ListChecks } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_OPTIONS = [
  { value: 'baixa', label: 'Baixa' },
  { value: 'média', label: 'Média' },
  { value: 'alta', label: 'Alta' },
  { value: 'crítica', label: 'Crítica' },
];

const SEVERITY_STYLES = {
  baixa: 'bg-blue-500 hover:bg-blue-500',
  'média': 'bg-yellow-500 hover:bg-yellow-500 text-black',
  alta: 'bg-orange-500 hover:bg-orange-500',
  'crítica': 'bg-red-600 hover:bg-red-600',
};

const REC_STATUS_OPTIONS = [
  { value: 'pendente', label: 'Pendente' },
  { value: 'em_andamento', label: 'Em andamento' },
  { value: 'implementada', label: 'Implementada' },
];

const REC_STATUS_STYLES = {
  pendente: 'border-gray-400 text-gray-600',
  em_andamento: 'border-blue-500 text-blue-600',
  implementada: 'border-green-500 text-green-600',
};

const EVAL_STATUS_OPTIONS = [
  { value: 'draft', label: 'Rascunho' },
  { value: 'in_progress', label: 'Em Andamento' },
  { value: 'finished', label: 'Finalizada' },
];

const EVAL_STATUS_STYLES = {
  draft: 'bg-gray-500',
  in_progress: 'bg-blue-500',
  finished: 'bg-green-500',
};

const emptyForm = {
  heuristic_definition_id: '',
  context: '',
  severity: 'média',
  notes: '',
  screenshot_url: '',
  personas_ids: [],
  recommendations: [],
};

export const HeuristicDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [evaluation, setEvaluation] = useState(null);
  const [definitions, setDefinitions] = useState([]);
  const [items, setItems] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const evalRes = await axios.get(`${API_URL}/api/heuristics/evaluations/${id}`);
      const projectId = evalRes.data.project_id;
      const [defsRes, itemsRes] = await Promise.all([
        axios.get(`${API_URL}/api/heuristics/definitions?project_id=${projectId}`),
        axios.get(`${API_URL}/api/heuristics/evaluations/${id}/items`),
      ]);
      setEvaluation(evalRes.data);
      setDefinitions(defsRes.data);
      setItems(itemsRes.data);
      // Personas for the project (optional association)
      try {
        const pRes = await axios.get(`${API_URL}/api/personas/projects/${projectId}/personas`);
        setPersonas(pRes.data || []);
      } catch (_) {
        setPersonas([]);
      }
    } catch (error) {
      toast.error('Erro ao carregar avaliação');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreateDialog = (definitionId) => {
    setEditingItemId(null);
    setForm({ ...emptyForm, heuristic_definition_id: definitionId });
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItemId(item.id);
    setForm({
      heuristic_definition_id: item.heuristic_definition_id,
      context: item.context || '',
      severity: item.severity || 'média',
      notes: item.notes || '',
      screenshot_url: item.screenshot_url || '',
      personas_ids: item.personas_ids || [],
      recommendations: (item.recommendations || []).map((r) => ({
        recommendation_text: r.recommendation_text,
        status: r.status,
      })),
    });
    setDialogOpen(true);
  };

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const addRecommendation = () =>
    updateForm({ recommendations: [...form.recommendations, { recommendation_text: '', status: 'pendente' }] });

  const updateRecommendation = (idx, patch) => {
    const next = form.recommendations.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    updateForm({ recommendations: next });
  };

  const removeRecommendation = (idx) =>
    updateForm({ recommendations: form.recommendations.filter((_, i) => i !== idx) });

  const togglePersona = (personaId) => {
    const set = new Set(form.personas_ids);
    if (set.has(personaId)) set.delete(personaId);
    else set.add(personaId);
    updateForm({ personas_ids: Array.from(set) });
  };

  const handleSaveItem = async () => {
    if (!form.context.trim()) {
      toast.error('Descreva o contexto do problema');
      return;
    }
    const payload = {
      heuristic_definition_id: form.heuristic_definition_id,
      context: form.context.trim(),
      severity: form.severity,
      notes: form.notes,
      screenshot_url: form.screenshot_url,
      personas_ids: form.personas_ids,
      recommendations: form.recommendations
        .filter((r) => r.recommendation_text.trim())
        .map((r) => ({ recommendation_text: r.recommendation_text.trim(), status: r.status })),
    };
    setSaving(true);
    try {
      if (editingItemId) {
        await axios.put(`${API_URL}/api/heuristics/items/${editingItemId}`, payload);
        toast.success('Issue atualizada!');
      } else {
        await axios.post(`${API_URL}/api/heuristics/evaluations/${id}/items`, payload);
        toast.success('Issue adicionada!');
      }
      setDialogOpen(false);
      await fetchData();
    } catch (error) {
      toast.error('Erro ao salvar issue');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/api/heuristics/items/${deleteTarget}`);
      toast.success('Issue excluída!');
      setDeleteTarget(null);
      await fetchData();
    } catch (error) {
      toast.error('Erro ao excluir issue');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API_URL}/api/heuristics/evaluations/${id}`, { status: newStatus });
      setEvaluation((prev) => ({ ...prev, status: newStatus }));
      toast.success('Status atualizado!');
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full"></div></div>;
  }

  const severityCounts = items.reduce(
    (acc, it) => {
      acc[it.severity] = (acc[it.severity] || 0) + 1;
      return acc;
    },
    { baixa: 0, 'média': 0, alta: 0, 'crítica': 0 }
  );

  const personaName = (pid) => personas.find((p) => p.id === pid)?.name || 'Persona';

  return (
    <div className="space-y-6" data-testid="heuristic-detail-page">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate('/heuristics')} data-testid="back-button">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <CardTitle className="text-2xl" data-testid="evaluation-title">{evaluation.title}</CardTitle>
              {evaluation.description && <p className="text-muted-foreground">{evaluation.description}</p>}
              {evaluation.related_url && (
                <a
                  href={evaluation.related_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                  data-testid="evaluation-url"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  {evaluation.related_url}
                </a>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Badge className={EVAL_STATUS_STYLES[evaluation.status]} data-testid="evaluation-status-badge">
                {EVAL_STATUS_OPTIONS.find((s) => s.value === evaluation.status)?.label}
              </Badge>
              <Select value={evaluation.status} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-[170px]" data-testid="status-select-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVAL_STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value} data-testid={`status-option-${s.value}`}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Summary */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-5" data-testid="severity-summary">
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <ListChecks className="h-4 w-4" /> Total de issues
            </div>
            <p className="text-3xl font-bold mt-1" data-testid="total-issues-count">{items.length}</p>
          </CardContent>
        </Card>
        {SEVERITY_OPTIONS.map((s) => (
          <Card key={s.value}>
            <CardContent className="pt-5">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <span className={`inline-block h-3 w-3 rounded-full ${SEVERITY_STYLES[s.value].split(' ')[0]}`}></span>
                {s.label}
              </div>
              <p className="text-3xl font-bold mt-1" data-testid={`severity-count-${s.value}`}>{severityCounts[s.value] || 0}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-bold">Heurísticas de Nielsen</h2>
        <Accordion type="multiple" className="space-y-4">
          {definitions.map((def) => {
            const defItems = items.filter((item) => item.heuristic_definition_id === def.id);
            return (
              <AccordionItem key={def.id} value={def.id} className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline" data-testid={`heuristic-trigger-${def.order_index}`}>
                  <div className="flex items-center justify-between w-full pr-4">
                    <span className="font-medium text-left">{def.title}</span>
                    {defItems.length > 0 && (
                      <Badge variant="secondary" className="ml-2 shrink-0">{defItems.length} issue(s)</Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-2">
                    <p className="text-sm text-muted-foreground">{def.description}</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openCreateDialog(def.id)}
                      data-testid={`add-issue-button-${def.order_index}`}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Adicionar Issue
                    </Button>

                    {defItems.length > 0 ? (
                      <div className="space-y-3">
                        {defItems.map((item) => (
                          <Card key={item.id} data-testid={`issue-card-${item.id}`}>
                            <CardContent className="pt-4">
                              <div className="flex items-start justify-between gap-3 mb-2">
                                <p className="font-medium">{item.context}</p>
                                <div className="flex items-center gap-2 shrink-0">
                                  <Badge className={`${SEVERITY_STYLES[item.severity]} capitalize`}>{item.severity}</Badge>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditDialog(item)} data-testid={`edit-issue-${item.id}`}>
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => setDeleteTarget(item.id)} data-testid={`delete-issue-${item.id}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </div>
                              {item.notes && <p className="text-sm text-muted-foreground mb-2 whitespace-pre-wrap">{item.notes}</p>}
                              {item.screenshot_url && (
                                <a href={item.screenshot_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline mb-2">
                                  <ExternalLink className="h-3 w-3" /> Ver screenshot
                                </a>
                              )}
                              {item.personas_ids && item.personas_ids.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-2">
                                  {item.personas_ids.map((pid) => (
                                    <Badge key={pid} variant="outline" className="text-xs">{personaName(pid)}</Badge>
                                  ))}
                                </div>
                              )}
                              {item.recommendations && item.recommendations.length > 0 && (
                                <div className="mt-3 pl-4 border-l-2 border-primary space-y-2">
                                  <p className="text-xs font-semibold uppercase text-muted-foreground">Recomendações</p>
                                  {item.recommendations.map((rec) => (
                                    <div key={rec.id} className="flex items-start justify-between gap-2">
                                      <p className="text-sm">{rec.recommendation_text}</p>
                                      <Badge variant="outline" className={`text-xs shrink-0 ${REC_STATUS_STYLES[rec.status]}`}>
                                        {REC_STATUS_OPTIONS.find((o) => o.value === rec.status)?.label || rec.status}
                                      </Badge>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-2 flex items-center justify-center gap-2">
                        <AlertTriangle className="h-4 w-4" /> Nenhuma issue registrada
                      </p>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>

      {/* Issue Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="issue-dialog">
          <DialogHeader>
            <DialogTitle>{editingItemId ? 'Editar Issue' : 'Adicionar Issue'}</DialogTitle>
            <DialogDescription>
              {definitions.find((d) => d.id === form.heuristic_definition_id)?.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Contexto do problema *</Label>
              <Textarea
                value={form.context}
                onChange={(e) => updateForm({ context: e.target.value })}
                placeholder="Descreva onde e qual é o problema observado"
                rows={3}
                data-testid="issue-context-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Severidade</Label>
                <Select value={form.severity} onValueChange={(v) => updateForm({ severity: v })}>
                  <SelectTrigger data-testid="issue-severity-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SEVERITY_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value} data-testid={`severity-option-${s.value}`}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>URL do screenshot (opcional)</Label>
                <Input
                  value={form.screenshot_url}
                  onChange={(e) => updateForm({ screenshot_url: e.target.value })}
                  placeholder="https://..."
                  data-testid="issue-screenshot-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Observações / Notas</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => updateForm({ notes: e.target.value })}
                placeholder="Detalhes adicionais, impacto, evidências..."
                rows={2}
                data-testid="issue-notes-input"
              />
            </div>

            {personas.length > 0 && (
              <div className="space-y-2">
                <Label>Personas impactadas</Label>
                <div className="flex flex-wrap gap-3 border rounded-md p-3">
                  {personas.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={form.personas_ids.includes(p.id)}
                        onCheckedChange={() => togglePersona(p.id)}
                        data-testid={`persona-checkbox-${p.id}`}
                      />
                      {p.name}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Recomendações</Label>
                <Button type="button" size="sm" variant="outline" onClick={addRecommendation} data-testid="add-recommendation-button">
                  <Plus className="mr-1 h-3.5 w-3.5" /> Adicionar
                </Button>
              </div>
              {form.recommendations.length === 0 && (
                <p className="text-sm text-muted-foreground">Nenhuma recomendação. Clique em "Adicionar".</p>
              )}
              <div className="space-y-3">
                {form.recommendations.map((rec, idx) => (
                  <div key={idx} className="flex items-start gap-2" data-testid={`recommendation-row-${idx}`}>
                    <Textarea
                      value={rec.recommendation_text}
                      onChange={(e) => updateRecommendation(idx, { recommendation_text: e.target.value })}
                      placeholder="Recomendação de melhoria"
                      rows={2}
                      className="flex-1"
                      data-testid={`recommendation-text-${idx}`}
                    />
                    <Select value={rec.status} onValueChange={(v) => updateRecommendation(idx, { status: v })}>
                      <SelectTrigger className="w-[150px] shrink-0" data-testid={`recommendation-status-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {REC_STATUS_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="ghost" size="icon" className="text-destructive shrink-0" onClick={() => removeRecommendation(idx)} data-testid={`remove-recommendation-${idx}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveItem} disabled={saving} data-testid="save-issue-button">
              {saving ? 'Salvando...' : editingItemId ? 'Salvar alterações' : 'Adicionar Issue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-issue-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir issue?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteItem} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-issue">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
